// src/pages/projects/ProjectManagementPage.js
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

const ProjectManagementPage = () => {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "projects";

    // -------- Upload --------
    const [file, setFile] = useState(null);
    const [createdBy, setCreatedBy] = useState("admin");
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(null);

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    // -------- Table --------
    const [projects, setProjects] = useState(() => store.getRows(CACHE_KEY));
    const [loadingTable, setLoadingTable] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [totalCount, setTotalCount] = useState(null);
    const [search, setSearch] = useState("");

    // 🔹 EDIT / UPDATE STATE
    const [editingProject, setEditingProject] = useState(null);



    // -------- Edit --------
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState(null); // full project from GET by id
    const [editForm, setEditForm] = useState({});   // patch payload
    const [saving, setSaving] = useState(false);

    // -------- Audit --------
    const [auditOpen, setAuditOpen] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditRows, setAuditRows] = useState([]);
    const [auditProjectId, setAuditProjectId] = useState("");



    // const EDIT_FIELDS = [
    //     { key: "projectName", label: "Project Name" },
    //     { key: "department", label: "Department" },
    //     { key: "scheme", label: "Scheme" },
    //     { key: "subScheme", label: "Sub Scheme" },
    //     { key: "financialModelType", label: "Financial Model Type" },
    //     { key: "projectFundedBy", label: "Project Funded By" },
    //     { key: "durationInYears", label: "Duration (Years)", type: "number" },
    //     { key: "assessmentMode", label: "Assessment Mode" },
    //     { key: "trainingTargets", label: "Training Targets", type: "number" },
    //     { key: "totalProjectTarget", label: "Total Project Target", type: "number" },
    //     { key: "status", label: "Status" },
    // ];

    const EDIT_FIELDS = [
        // 🔹 Basic Project Info
        { key: "projectName", label: "Project Name" },
        { key: "projectType", label: "Project Type" },
        { key: "department", label: "Department" },
        { key: "scheme", label: "Scheme" },
        { key: "subScheme", label: "Sub Scheme" },

        // 🔹 Funding & Model
        { key: "financialModelType", label: "Financial Model Type" },
        { key: "projectFundedBy", label: "Project Funded By" },

        // 🔹 Dates
        { key: "agreementDate", label: "Agreement Date", type: "date" },
        { key: "projectStartDate", label: "Project Start Date", type: "date" },
        { key: "projectEndDate", label: "Project End Date", type: "date" },

        // 🔹 Targets
        { key: "durationInYears", label: "Duration (Years)", type: "number" },
        { key: "trainingTargets", label: "Training Targets", type: "number" },
        { key: "placementTargets", label: "Placement Targets", type: "number" },
        { key: "totalProjectTarget", label: "Total Project Target", type: "number" },

        // 🔹 Assessment & Agreement
        { key: "assessmentMode", label: "Assessment Mode" },
        { key: "signedAgreement", label: "Signed Agreement" },

        // 🔹 Status & Remarks

        { key: "remarks", label: "Remarks" },
    ];



    // -------- Message --------
    const [msg, setMsg] = useState({ type: "", text: "" });

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }),
        [token]
    );

    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : null;
        } catch {
            return text;
        }
    };

    const encodeId = (projectId) => encodeURIComponent(projectId); // keeps / safe

    // ------------------ 3) GET ALL PROJECTS ------------------

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setProjects(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoadingTable(true);
        try {
            const res = await fetch(`${BASE_URL}/api/projects`, { headers: authHeaders });
            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Failed to load projects");
            const all = Array.isArray(data) ? data : [];
            setProjects(all);
            setAllLoaded(true);
            store.setRows(CACHE_KEY, all, true);
        } catch (e) {
            flash("err", e.message || "Failed to load projects");
        } finally {
            setLoadingTable(false);
        }
    };

    const fetchAllProjects = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(`${BASE_URL}/api/projects`, { headers: authHeaders });
            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Failed to load projects");
            const all = Array.isArray(data) ? data : [];
            setProjects(all);
            setAllLoaded(true);
            store.markAllLoaded(CACHE_KEY, all);
        } catch (e) {
            flash("err", e.message);
        } finally {
            setLoadingAll(false);
        }
    };

    // Keep fetchProjects as an alias for refreshing after mutations
    const fetchProjects = fetchAllProjects;

    useEffect(() => {
        fetchFirst100();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.projects ?? null); })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const onEditChange = (key, value) => {
        setEditForm(prev => ({
            ...prev,
            [key]: value
        }));
    };


    const validateAndNormalizeExcel = async (file) => {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        console.group("📘 Excel Validation Debug");
        console.log("Total rows:", rows.length);

        rows.forEach((row, idx) => {
            const rowNo = idx + 2; // header row = 1

            const raw =
                row["Duration in Years"] ??
                row["durationInYears"] ??
                "";

            if (raw === "" || raw == null) {
                throw new Error(`Row ${rowNo}: Duration in Years is required`);
            }

            const num = Number(raw);

            if (Number.isNaN(num)) {
                throw new Error(`Row ${rowNo}: Duration in Years must be numeric`);
            }

            row["Duration in Years"] = num;
        });

        console.groupEnd();
        return rows;
    };


    // ------------------ 1) UPLOAD EXCEL (BULK UPSERT) ------------------

    const uploadExcel = async () => {
        if (!file) return flash("err", "Please select an Excel file first");
        let headerRow, dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            if (raw.length < 2) return flash("err", "Excel file is empty or has no data rows");
            headerRow = raw[0];
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";
        let allErrors = [];
        let totalSkippedNoChange = 0;
        let totalSkippedInvalid = 0;
        const uploadOneChunk = async (i) => {
            const chunkRows = [headerRow, ...dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
            const res = await fetch(`${BASE_URL}/api/projects/upload-json?actor=Rahul`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return {
                inserted: data?.inserted ?? 0,
                skipped: data?.skipped ?? 0,
                skippedNoChange: data?.skippedNoChange ?? 0,
                skippedInvalid: data?.skippedInvalid ?? 0,
                errors: data?.errors ?? [],
            };
        };
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    totalSkippedNoChange += result.value.skippedNoChange;
                    totalSkippedInvalid += result.value.skippedInvalid;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                    if (result.value.errors?.length) allErrors = [...allErrors, ...result.value.errors];
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }
        setUploading(false);
        if (!failed) {
            setUploadResult({ totalInserted, totalSkipped, totalSkippedNoChange, totalSkippedInvalid, errors: allErrors });
            const msg = `Upload complete — Inserted: ${totalInserted}, Updated: 0, Skipped (no change): ${totalSkippedNoChange}, Skipped (invalid): ${totalSkippedInvalid}`;
            flash(allErrors.length ? "err" : "ok", msg);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllProjects();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };


    // ------------------ 2) DOWNLOAD TEMPLATE ------------------

    // const downloadTemplate = async () => {
    //     try {
    //         const res = await fetch(`${BASE_URL}/api/projects/template`, {
    //             headers: authHeaders,
    //         });

    //         if (!res.ok) {
    //             const data = await safeJson(res);
    //             throw new Error(data?.message || "Template download failed");
    //         }

    //         const blob = await res.blob();
    //         const url = window.URL.createObjectURL(blob);
    //         const a = document.createElement("a");
    //         a.href = url;
    //         a.download = "create_project_template.xlsx";
    //         document.body.appendChild(a);
    //         a.click();
    //         a.remove();
    //         window.URL.revokeObjectURL(url);
    //     } catch (e) {
    //         flash("err", e.message);
    //     }
    // };

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/create_project_template.xlsx"; // local path
        a.download = "create_project_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    // ------------------ Local Excel Download (All Table Data) ------------------

    const [exporting, setExporting] = useState(false);

    const downloadTableExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = projects;
            if (!allLoaded) {
                const res = await fetch(`${BASE_URL}/api/projects`, { headers: authHeaders });
                const json = await safeJson(res);
                data = Array.isArray(json) ? json : [];
                setProjects(data);
                setAllLoaded(true);
                store.setRows(CACHE_KEY, data, true);
            }
            if (!data.length) return flash("err", "No projects to download");
            const exportRows = data.map((p) => {
                const out = {};
                if (p.projectId !== undefined) out.projectId = p.projectId;
                Object.keys(p).forEach((k) => {
                    if (k === "projectId") return;
                    out[k] = typeof p[k] === "object" ? JSON.stringify(p[k]) : p[k];
                });
                return out;
            });
            const ws = XLSX.utils.json_to_sheet(exportRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Projects");
            XLSX.writeFile(wb, "projects.xlsx");
            flash("ok", `Exported ${data.length.toLocaleString()} records`);
        } catch (e) {
            flash("err", "Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    // ------------------ 4) GET SINGLE PROJECT (EDIT) ------------------

    const openEdit = async (row) => {
        console.log(row)
        if (!row?.projectId) return;
        console.log(row.projectId)

        try {
            // const res = await fetch(
            //     `${BASE_URL}/projects?projectId=${encodeURIComponent(row.projectId)}`,
            //     { headers: { Authorization: `Bearer ${token}` } }
            // );

            // console.log(`${BASE_URL}/api/projects?projectId=${encodeURIComponent(row.projectId)}`)

            // const raw = await safeJson(res);
            // const data = Array.isArray(raw) ? raw[0] : raw;

            // if (!res.ok) {
            //     throw new Error(data?.message || "Failed to load project");
            // }

            // ✅ OPEN MODAL
            setEditingProject(row);
            console.log(row)
            // ✅ AUTO-POPULATE ALL FIELDS FROM EDIT_FIELDS
            const form = {};
            EDIT_FIELDS.forEach(f => {
                form[f.key] = row[f.key] ?? "";
            });

            setEditForm(form);

        } catch (e) {
            alert("❌ " + e.message);
        }
    };



    // const updateProject = async () => {
    //     if (!editingProject?.projectId) return;

    //     const url = `${BASE_URL}/projects?projectId=${editingProject.projectId
    //         }`;
    //     console.log(url)

    //     try {
    //         const res = await fetch(url, {
    //             method: "PATCH", // preferred
    //             headers: {
    //                 "Content-Type": "application/json",
    //                 Authorization: `Bearer ${token}`,
    //             },
    //             body: JSON.stringify(editForm),
    //         });

    //         const data = await safeJson(res);
    //         console.log(data)

    //         if (!res.ok) {
    //             throw new Error(data?.message || "Update failed");
    //         }

    //         alert("✅ Project updated");

    //         setEditingProject(null);
    //         fetchProjects();
    //     } catch (e) {
    //         alert("❌ " + e.message);
    //     }
    // };


    const updateProject = async () => {
        if (!editingProject?.projectId) return;

        const cleanedPayload = {};

        Object.entries(editForm).forEach(([key, value]) => {
            if (value === "" || value === null || value === undefined) return;

            // convert numeric fields properly
            if (
                [
                    "durationInYears",
                    "trainingTargets",
                    "placementTargets",
                    "totalProjectTarget"
                ].includes(key)
            ) {
                cleanedPayload[key] = Number(value);
            } else {
                cleanedPayload[key] = value;
            }
        });

        try {
            const res = await fetch(
                `${BASE_URL}/api/projects?projectId=${encodeURIComponent(
                    editingProject.projectId
                )}`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(cleanedPayload),
                }
            );

            const data = await safeJson(res);

            if (!res.ok) {
                throw new Error(data?.message || "Update failed");
            }

            alert("✅ Project updated");

            setEditingProject(null);
            store.invalidate(CACHE_KEY);
            fetchProjects();
        } catch (e) {
            alert("❌ " + e.message);
        }
    };



    const closeEdit = () => {
        setEditingId(null);
        setEditData(null);
        setEditForm({});
    };

    const onPatchChange = (key, value) => {
        setEditForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    // build field list from editData (exclude projectId)
    const editableKeys = useMemo(() => {
        if (!editData) return [];
        return Object.keys(editData).filter((k) => k !== "projectId");
    }, [editData]);

    // helper: if user clears input, remove key from patch payload
    const setPatchValue = (key, rawValue) => {
        const value = rawValue;
        setEditForm((prev) => {
            // treat empty string as "not provided"
            if (value === "" || value === null || value === undefined) {
                const copy = { ...prev };
                delete copy[key];
                return copy;
            }
            return { ...prev, [key]: value };
        });
    };

    // ------------------ 5) PATCH PROJECT (RECOMMENDED) ------------------

    const patchProject = async () => {
        if (!editingId) return;
        if (!Object.keys(editForm).length) {
            flash("err", "No changes to update (patch payload is empty)");
            return;
        }

        setSaving(true);
        try {
            // const res = await fetch(`${BASE_URL}/api/projects/${encodeId(editingId)}`, {
            //     method: "PATCH",
            //     headers: jsonHeaders,
            //     body: JSON.stringify(editForm),
            // });

            const res = await fetch(
                `${BASE_URL}/api/projects?projectId=${encodeURIComponent(editingId)}`,
                {
                    method: "PATCH",
                    headers: jsonHeaders,
                    body: JSON.stringify(editForm),
                }
            );

            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Patch failed");

            flash("ok", "Project updated (PATCH)");
            closeEdit();
            store.invalidate(CACHE_KEY);
            fetchProjects();
        } catch (e) {
            flash("err", e.message);
        } finally {
            setSaving(false);
        }
    };

    // ------------------ 6) PUT PROJECT (LEGACY) ------------------

    const putProject = async () => {
        if (!editingId) return;

        // Legacy PUT still accepts partial update in your backend, but we keep same payload
        if (!Object.keys(editForm).length) {
            flash("err", "No changes to update (payload is empty)");
            return;
        }

        setSaving(true);
        try {
            // const res = await fetch(`${BASE_URL}/api/projects/${encodeId(editingId)}`, {
            //     method: "PUT",
            //     headers: jsonHeaders,
            //     body: JSON.stringify(editForm),
            // });
            const res = await fetch(
                `${BASE_URL}/api/projects?projectId=${encodeURIComponent(editingId)}`,
                {
                    method: "PUT",
                    headers: jsonHeaders,
                    body: JSON.stringify(editForm),
                }
            );

            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Put failed");

            flash("ok", "Project updated (PUT)");
            closeEdit();
            store.invalidate(CACHE_KEY);
            fetchProjects();
        } catch (e) {
            flash("err", e.message);
        } finally {
            setSaving(false);
        }
    };

    // ------------------ 7) SOFT DELETE (DEACTIVATE) ------------------

    const deactivateProject = async (projectId) => {
        if (!window.confirm("Deactivate this project?")) return;

        try {
            const res = await fetch(
                `${BASE_URL}/api/projects?projectId=${encodeURIComponent(projectId)}`,
                {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const data = await safeJson(res);

            if (!res.ok) {
                throw new Error(data?.message || "Deactivate failed");
            }

            alert("✅ Project deactivated");
            store.invalidate(CACHE_KEY);
            fetchProjects();
        } catch (e) {
            alert("❌ " + e.message);
        }
    };

    const fetchAuditLogs = async (projectId) => {
        const res = await fetch(
            `${BASE_URL}/api/project-audits?projectId=${encodeURIComponent(projectId)}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );

        return safeJson(res);
    };


    // ------------------ 8) GET PROJECT AUDIT LOGS ------------------

    const openAudit = async (projectId) => {
        setAuditProjectId(projectId);
        setAuditOpen(true);
        setAuditRows([]);
        setAuditLoading(true);

        try {
            // const res = await fetch(`${BASE_URL}/api/project-audits?projectId=${projectId}`, {

            //     headers: authHeaders,
            // });

            const res = await fetch(
                `${BASE_URL}/api/project-audits?projectId=${encodeURIComponent(projectId)}`,
                {
                    headers: authHeaders,
                }
            );

            const data = await safeJson(res);
            if (!res.ok) throw new Error(data?.message || "Failed to load audits");
            setAuditRows(Array.isArray(data) ? data : []);
        } catch (e) {
            flash("err", e.message);
        } finally {
            setAuditLoading(false);
        }
    };

    const closeAudit = () => {
        setAuditOpen(false);
        setAuditRows([]);
        setAuditProjectId("");
    };

    // ------------------ SEARCH + TABLE ------------------
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        return projects
            // ✅ HIDE SOFT-DELETED
            .filter(p => p.isDeleted !== true)

            // ✅ HIDE INACTIVE
            .filter(p => String(p.status || "").toUpperCase() !== "INACTIVE")

            // 🔍 SEARCH
            .filter((p) => {
                if (!q) return true;

                const id = String(p.projectId || "").toLowerCase();
                const name = String(p.projectName || "").toLowerCase();
                const dept = String(p.department || "").toLowerCase();
                const scheme = String(p.scheme || "").toLowerCase();
                const type = String(p.projectType || "").toLowerCase();
                const status = String(p.status || "").toLowerCase();

                return (
                    id.includes(q) ||
                    name.includes(q) ||
                    dept.includes(q) ||
                    scheme.includes(q) ||
                    type.includes(q) ||
                    status.includes(q)
                );
            });
    }, [projects, search]);


    const columns = useMemo(() => {
        const sample = filtered[0] || projects[0];
        if (!sample) return [];

        const keys = Object.keys(sample);

        // ensure projectId first
        const priority = ["projectId"];
        const rest = keys.filter((k) => !priority.includes(k));
        return [...priority.filter((k) => keys.includes(k)), ...rest];
    }, [filtered, projects]);

    // ------------------ UI ------------------

    return (
        <div style={styles.page}>
            <div style={styles.headerRow}>
                <div>
                    <div style={styles.title}>Projects</div>
                    {/* <div style={styles.subTitle}>
                        Upload Excel (bulk upsert), manage projects, patch updates, deactivate, audit logs
                    </div> */}
                </div>

                <button style={styles.refreshBtn} onClick={fetchProjects} disabled={loadingTable}>
                    {loadingTable ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            {msg.text ? (
                <div style={{ ...styles.flash, ...(msg.type === "ok" ? styles.flashOk : styles.flashErr), display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            ) : null}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            {/* 1) Upload Excel */}
            <div style={styles.card}>
                <div style={styles.cardTitle}>1️⃣ Upload Excel (Bulk Upsert)</div>

                <div style={styles.uploadGrid}>
                    <div style={styles.field}>
                        <label style={styles.label}>Excel File *</label>
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            style={styles.inputFile}
                        />
                        <div style={styles.helper}>
                            {file ? `Selected: ${file.name}` : "Choose .xlsx / .xls file"}
                        </div>
                    </div>

                    {/* <div style={styles.field}>
                        <label style={styles.label}>createdBy</label>
                        <input
                            style={styles.input}
                            value={createdBy}
                            onChange={(e) => setCreatedBy(e.target.value)}
                            placeholder="admin / SYSTEM / username"
                        />
                        <div style={styles.helper}>
                            .
                        </div>
                    </div> */}

                    <div style={{ ...styles.field, alignItems: "flex-end", marginBottom: "30px" }}>
                        <button style={styles.primaryBtn} onClick={uploadExcel} disabled={uploading}>
                            {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload"}
                        </button>

                    </div>

                </div>

                {/* 2) Download Template */}
                <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
                    <button style={styles.secondaryBtn} onClick={downloadTemplate}>
                        Download Excel Template
                    </button>

                    {/* <button style={styles.secondaryBtn} onClick={downloadTableExcel} disabled={!projects.length}>
                        Download Table Excel
                    </button> */}
                </div>

                {/* Upload Result */}
                {uploadResult ? (
                    <div style={styles.resultBox}>
                        <div style={styles.resultTitle}>Upload Result</div>

                        <div style={styles.resultGrid4}>
                            <div style={styles.kv}>
                                <span style={styles.k}>Inserted</span>
                                <span style={{ ...styles.v, color: "#065f46" }}>{uploadResult.totalInserted ?? 0}</span>
                            </div>
                            <div style={styles.kv}>
                                <span style={styles.k}>Skipped (No Change)</span>
                                <span style={{ ...styles.v, color: "#92400e" }}>{uploadResult.totalSkippedNoChange ?? 0}</span>
                            </div>
                            <div style={styles.kv}>
                                <span style={styles.k}>Skipped (Invalid / Error)</span>
                                <span style={{ ...styles.v, color: "#991b1b" }}>{uploadResult.totalSkippedInvalid ?? 0}</span>
                            </div>
                            <div style={styles.kv}>
                                <span style={styles.k}>Total Skipped</span>
                                <span style={styles.v}>{uploadResult.totalSkipped ?? 0}</span>
                            </div>
                        </div>

                        {Array.isArray(uploadResult.errors) && uploadResult.errors.length ? (
                            <div style={{ marginTop: 10 }}>
                                <div style={{ ...styles.resultTitle, color: "#991b1b" }}>
                                    Row Errors ({uploadResult.errors.length})
                                </div>
                                <div style={styles.errorList}>
                                    {uploadResult.errors.map((er, idx) => (
                                        <div key={idx} style={styles.errorItem}>
                                            <span style={{ fontWeight: 900 }}>Row {er.rowNumber}</span>
                                            {er.field ? ` [${er.field}]` : ""}
                                            {er.projectId ? ` — ${er.projectId}` : ""}
                                            : {er.message}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div style={styles.okNote}>No row errors ✅</div>
                        )}
                    </div>
                ) : null}
            </div>

            {/* 3) Projects Table */}
            <div style={styles.card}>
                <div style={styles.cardTop}>
                    <div style={styles.cardTitle}>Projects Table</div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <input
                            style={{ ...styles.input, width: 340 }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by ID / Name / Dept / Scheme / Type / Status..."
                        />
                        <button style={styles.secondaryBtn} onClick={downloadTableExcel} disabled={!projects.length || exporting}>
                            {exporting ? "Processing..." : "Export Excel"}
                        </button>
                    </div>
                </div>
                {totalCount !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                        Total: {totalCount.toLocaleString()}
                    </div>
                )}

                <div style={styles.tableWrap}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: 48, textAlign: "center" }}>#</th>
                                {columns.length ? (
                                    columns.map((c) => (
                                        <th key={c} style={styles.th}>
                                            {c}
                                        </th>
                                    ))
                                ) : (
                                    <th style={styles.th}>Projects</th>
                                )}
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.length === 0 && !loadingTable && (
                                <tr>
                                    <td
                                        style={styles.td}
                                        colSpan={Math.max(columns.length + 1, 2)}
                                    >
                                        {loadingTable ? "Loading..." : "No projects found"}
                                    </td>
                                </tr>
                            )}
                            {filtered.length === 0 && loadingTable && (
                                <tr>
                                    <td style={styles.td} colSpan={Math.max(columns.length + 1, 2)}>Loading...</td>
                                </tr>
                            )}
                            {filtered.length > 0 && (
                                filtered.map((p, idx) => (
                                    <tr key={p.projectId || idx}>
                                        <td style={{ ...styles.td, textAlign: "center", color: "#9ca3af", fontWeight: 700 }}>{idx + 1}</td>
                                        {columns.map((c) => (
                                            <td key={c} style={styles.td}>
                                                {typeof p[c] === "object"
                                                    ? JSON.stringify(p[c])
                                                    : String(p[c] ?? "")}
                                            </td>
                                        ))}

                                        {/* ✅ ACTIONS COLUMN */}
                                        <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                                            {/* <button
                                                style={styles.smallBtn}
                                                onClick={() => openEdit(p)}
                                            >
                                                Edit
                                            </button> */}

                                            {/* <button
                                                style={styles.smallBtn}
                                                onClick={() => openAudit(p.projectId)}
                                            >
                                                Audit
                                            </button> */}

                                            <button
                                                style={{ ...styles.smallBtn, ...styles.dangerBtn }}
                                                onClick={() => deactivateProject(p.projectId)}
                                            >
                                                Deactivate
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                            {!allLoaded && !loadingTable && (
                                <tr>
                                    <td style={{ ...styles.td, textAlign: "center" }} colSpan={Math.max(columns.length + 2, 3)}>
                                        <button style={styles.secondaryBtn} onClick={fetchAllProjects} disabled={loadingAll}>
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>

                    </table>
                </div>
            </div>

            {/* 4/5/6) Edit Modal */}
            {editingId && (
                <div style={styles.modalOverlay} onMouseDown={closeEdit}>
                    <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <div>
                                <div style={styles.modalTitle}>Edit Project</div>
                                <div style={styles.modalSub}>
                                    ID: <b>{editingId}</b> (PATCH recommended)
                                </div>
                            </div>
                            <button style={styles.iconBtn} onClick={closeEdit} title="Close">
                                ✕
                            </button>
                        </div>

                        {!editData ? (
                            <div style={styles.muted}>Loading project...</div>
                        ) : (
                            <>
                                <div style={styles.infoBox}>
                                    <div style={styles.infoRow}>
                                        <span style={styles.k}>Current Name</span>
                                        <span style={styles.v}>{editData.projectName || "-"}</span>
                                    </div>
                                    <div style={styles.infoRow}>
                                        <span style={styles.k}>Current Status</span>
                                        <span style={styles.v}>{editData.status || "-"}</span>
                                    </div>
                                </div>

                                <div style={styles.formGrid}>
                                    {editableKeys.map((k) => {
                                        const current = editData[k] ?? "";
                                        const patchVal = editForm[k];

                                        return (
                                            <div key={k} style={styles.field}>
                                                <label style={styles.label}>
                                                    {k}
                                                    <span style={styles.currentHint}>
                                                        current: {String(current)}
                                                    </span>
                                                </label>

                                                <input
                                                    style={styles.input}
                                                    value={patchVal ?? ""}
                                                    placeholder="(leave blank to not change)"
                                                    onChange={(e) => setPatchValue(k, e.target.value)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                <div style={styles.patchPreview}>
                                    <div style={styles.resultTitle}>PATCH Payload Preview</div>
                                    <pre style={styles.pre}>
                                        {JSON.stringify(editForm, null, 2)}
                                    </pre>
                                </div>

                                <div style={styles.modalActions}>
                                    <button style={styles.secondaryBtn} onClick={closeEdit} disabled={saving}>
                                        Cancel
                                    </button>
                                    <button style={styles.primaryBtn} onClick={patchProject} disabled={saving}>
                                        {saving ? "Saving..." : "PATCH Update"}
                                    </button>
                                    <button style={styles.secondaryBtn} onClick={putProject} disabled={saving}>
                                        PUT (Legacy)
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* 8) Audit Modal */}
            {auditOpen && (
                <div style={styles.modalOverlay} onMouseDown={closeAudit}>
                    <div style={styles.modalWide} onMouseDown={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <div>
                                <div style={styles.modalTitle}>Project Audit Logs</div>
                                <div style={styles.modalSub}>
                                    Project: <b>{auditProjectId}</b>
                                </div>
                            </div>
                            <button style={styles.iconBtn} onClick={closeAudit} title="Close">
                                ✕
                            </button>
                        </div>

                        {auditLoading ? (
                            <div style={styles.muted}>Loading audits...</div>
                        ) : auditRows.length === 0 ? (
                            <div style={styles.muted}>No audit logs found</div>
                        ) : (
                            <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>ID</th>
                                            <th style={styles.th}>Action</th>
                                            <th style={styles.th}>Changed By</th>
                                            <th style={styles.th}>Changed At</th>
                                            <th style={styles.th}>Changes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auditRows.map((a) => (
                                            <tr key={a.id}>
                                                <td style={styles.td}>{a.id}</td>
                                                <td style={styles.td}>{a.action}</td>
                                                <td style={styles.td}>{a.changedBy}</td>
                                                <td style={styles.td}>{a.changedAt}</td>
                                                <td style={styles.td}>
                                                    <pre style={styles.preSmall}>
                                                        {typeof a.changesJson === "object"
                                                            ? JSON.stringify(a.changesJson, null, 2)
                                                            : String(a.changesJson ?? "")}
                                                    </pre>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>


            )}



            {editingProject && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalCard}>

                        {/* HEADER */}
                        <div style={styles.modalHeader}>
                            <div>
                                <div style={styles.modalTitle}>Edit Project</div>
                                <div style={styles.modalSub}>
                                    Project ID: <b>{editingProject.projectId}</b>
                                </div>
                            </div>

                            <button
                                style={styles.closeBtn}
                                onClick={() => setEditingProject(null)}
                            >
                                ✕
                            </button>
                        </div>

                        {/* BODY */}
                        <div style={styles.modalBody}>
                            <div style={styles.formGrid}>
                                {EDIT_FIELDS.map((f) => (
                                    <div key={f.key} style={styles.formField}>
                                        <label style={styles.label}>{f.label}</label>
                                        <input
                                            type={f.type || "text"}
                                            style={styles.input}
                                            value={editForm[f.key] ?? ""}
                                            onChange={(e) =>
                                                setEditForm(prev => ({
                                                    ...prev,
                                                    [f.key]: e.target.value
                                                }))
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* FOOTER */}
                        <div style={styles.modalFooter}>
                            <button
                                style={styles.secondaryBtn}
                                onClick={() => setEditingProject(null)}
                            >
                                Cancel
                            </button>

                            <button
                                style={styles.primaryBtn}
                                onClick={updateProject}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}




        </div>


    );
};

/* ---------------- INLINE CSS ---------------- */

const styles = {

    modalOverlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,

    },

    modalCard: {
        background: "#fff",
        width: "720px",
        maxHeight: "90vh",
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        padding: "15px",
    },

    modalHeader: {
        padding: "18px 20px",
        borderBottom: "1px solid #e5e7eb",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },

    modalTitle: {
        fontSize: "18px",
        fontWeight: 900,
    },

    modalSub: {
        fontSize: "12px",
        color: "#6b7280",
        marginTop: 4,
    },

    closeBtn: {
        border: "none",
        background: "#f3f4f6",
        borderRadius: "8px",
        padding: "6px 10px",
        cursor: "pointer",
        fontSize: "14px",
    },

    modalBody: {
        padding: "20px",
        overflowY: "auto",
    },

    formGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "14px 16px",
    },

    formField: {
        display: "flex",
        flexDirection: "column",
    },

    modalFooter: {
        padding: "14px 20px",
        borderTop: "1px solid #e5e7eb",
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        background: "#fafafa",
    },


    page: {
        padding: 24,
        background: "#f7f7fb",
        minHeight: "100vh",
        fontFamily: "Inter, system-ui, Arial",
        color: "#111827",
    },

    headerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        gap: 12,
        marginBottom: 14,
    },
    title: { fontSize: 28, fontWeight: 900, letterSpacing: 0.2 },
    subTitle: { marginTop: 4, color: "#6b7280", fontSize: 13 },

    refreshBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 900,
    },

    flash: {
        marginBottom: 14,
        padding: "10px 12px",
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 800,
    },
    flashOk: { background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46" },
    flashErr: { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" },

    card: {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        padding: 16,
        marginBottom: 14,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    },
    cardTop: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginBottom: 10,
    },
    cardTitle: { fontSize: 16, fontWeight: 900, marginBottom: 10 },

    uploadGrid: {
        display: "grid",
        gridTemplateColumns: "1.6fr 1fr",
        gap: 12,
        alignItems: "end",
    },

    formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
        gap: 12,
        marginTop: 12,
    },

    field: { display: "flex", flexDirection: "column", gap: 6 },
    label: { fontSize: 12, color: "#6b7280", fontWeight: 900 },

    input: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 12px",
        outline: "none",
        fontSize: 14,
        background: "#fff",
    },
    inputFile: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 12px",
        background: "#fff",
    },
    helper: { fontSize: 12, color: "#6b7280" },

    currentHint: {
        marginLeft: 8,
        fontSize: 11,
        fontWeight: 800,
        color: "#9ca3af",
    },

    primaryBtn: {
        border: "1px solid #2563eb",
        background: "#2563eb",
        color: "#fff",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 900,
        whiteSpace: "nowrap",
    },
    secondaryBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        color: "#111827",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 900,
        whiteSpace: "nowrap",
    },

    smallBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        padding: "7px 10px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 900,
        marginRight: 8,
    },
    dangerBtn: { borderColor: "#fecaca", color: "#991b1b", background: "#fff5f5" },

    iconBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        borderRadius: 10,
        padding: "6px 10px",
        cursor: "pointer",
        fontWeight: 900,
    },

    muted: { marginTop: 10, color: "#6b7280", fontSize: 13, fontWeight: 800 },

    resultBox: {
        marginTop: 12,
        border: "1px dashed #e5e7eb",
        borderRadius: 12,
        padding: 12,
        background: "#fafafa",
    },
    resultTitle: { fontSize: 13, fontWeight: 900, marginBottom: 8 },
    resultGrid4: {
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(160px, 1fr))",
        gap: 10,
    },
    kv: {
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        padding: "8px 10px",
        background: "#fff",
        border: "1px solid #eee",
        borderRadius: 10,
    },
    k: { color: "#6b7280", fontWeight: 900, fontSize: 12 },
    v: { fontWeight: 900, fontSize: 12 },

    okNote: { marginTop: 10, color: "#065f46", fontWeight: 900, fontSize: 12 },

    errorList: {
        border: "1px solid #fecaca",
        background: "#fff5f5",
        borderRadius: 12,
        padding: 10,
    },
    errorItem: {
        fontSize: 12,
        color: "#991b1b",
        fontWeight: 800,
        padding: "6px 0",
        borderBottom: "1px solid rgba(153,27,27,0.15)",
    },

    tableWrap: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
        textAlign: "left",
        fontSize: 12,
        color: "#6b7280",
        fontWeight: 900,
        padding: "10px 10px",
        borderBottom: "1px solid #e5e7eb",
        background: "#fcfcff",
        whiteSpace: "nowrap",
    },
    td: {
        padding: "10px 10px",
        borderBottom: "1px solid #f1f5f9",
        fontSize: 13,
        whiteSpace: "nowrap",
        verticalAlign: "top",
    },

    pre: {
        margin: 0,
        background: "#0b1220",
        color: "#e5e7eb",
        padding: 10,
        borderRadius: 10,
        fontSize: 12,
        overflowX: "auto",
    },
    preSmall: {
        margin: 0,
        background: "#0b1220",
        color: "#e5e7eb",
        padding: 8,
        borderRadius: 10,
        fontSize: 11,
        overflowX: "auto",
        maxWidth: 520,
    },

    infoBox: {
        marginTop: 10,
        border: "1px solid #e5e7eb",
        background: "#fafafa",
        borderRadius: 12,
        padding: 10,
    },
    infoRow: {
        display: "grid",
        gridTemplateColumns: "140px 1fr",
        gap: 10,
        padding: "6px 0",
        borderBottom: "1px solid #eee",
    },

    patchPreview: {
        marginTop: 12,
        border: "1px dashed #e5e7eb",
        background: "#fafafa",
        borderRadius: 12,
        padding: 10,
    },

    modalOverlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(17,24,39,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
    },
    modal: {
        width: "min(920px, 100%)",
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #e5e7eb",
        padding: 16,
        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
        maxHeight: "90vh",
        overflowY: "auto",
    },
    modalWide: {
        width: "min(1100px, 100%)",
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #e5e7eb",
        padding: 16,
        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
        maxHeight: "90vh",
        overflowY: "auto",
    },
    modalHeader: {
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        marginBottom: 8,
    },
    modalTitle: { fontSize: 16, fontWeight: 900 },
    modalSub: { fontSize: 12, color: "#6b7280", fontWeight: 800 },

    modalActions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
        marginTop: 14,
    },


};

export default ProjectManagementPage;
